<template>
  <div>
    <!----------------- Main contents Area --------------------->
    <main class="mt-3">
      <div class="container">
        <div class="alert alert-danger text-center" role="alert">
          <b class="fs-4">인스타그램 게시리스트</b>
        </div>

        <!-- 상단: 노출현황 + 등록버튼 -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="text-muted">
            전체 <b>{{ instaList.length }}</b>건 /
            메인노출 <b class="text-success">{{ activeCount }}</b>건
            <span class="ms-2 small">※ 위에서부터 순서대로 메인에 표시됩니다 (게시중만)</span>
          </div>
          <button type="button" class="btn btn-danger" @click="toggleInsertForm">
            {{ showInsertForm ? '등록닫기' : '링크등록' }}
          </button>
        </div>

        <!-- 링크 등록폼 -->
        <div class="card mb-3 ig-insert-card" v-if="showInsertForm">
          <div class="card-body">
            <div class="row g-2 align-items-end">
              <div class="col-md-3">
                <label class="form-label fw-bold">게시명 <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model.trim="newTitle" maxlength="100"
                       placeholder="관리용 이름 (예: 10월 오사카 릴스)">
              </div>
              <div class="col-md-7">
                <label class="form-label fw-bold">인스타그램 링크 <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model.trim="newUrl"
                       placeholder="인스타에서 '링크 복사'한 주소를 그대로 붙여넣기"
                       @keyup.enter="insertInsta">
              </div>
              <div class="col-md-2 d-grid">
                <button type="button" class="btn btn-primary" @click="insertInsta" :disabled="saving">
                  {{ saving ? '저장중...' : '저장' }}
                </button>
              </div>
            </div>

            <!-- 링크 인식결과 -->
            <div class="mt-2 small" v-if="newUrl">
              <span v-if="parsedNewUrl" class="text-success">
                <i class="bi bi-check-circle-fill"></i> 인식됨 :
                <span class="badge mx-1" :class="typeBadge(parsedNewUrl.type)">{{ typeLabel(parsedNewUrl.type) }}</span>
                <a :href="parsedNewUrl.url" target="_blank" rel="noopener">{{ parsedNewUrl.url }}</a>
              </span>
              <span v-else class="text-danger">
                <i class="bi bi-x-circle-fill"></i> 인스타그램 게시물/릴스 주소가 아닙니다
              </span>
            </div>
            <div class="mt-1 small text-muted">
              ※ 새로 등록한 링크는 맨 위(1번)에 추가됩니다<br>
              ※ 인스타 음악 라이브러리 곡이 들어간 릴스는 홈페이지에서 재생되지 않고 「Instagram에서 보기」로 표시됩니다
            </div>
          </div>
        </div>

        <!-- 순서 변경 저장바 -->
        <div class="alert alert-warning d-flex justify-content-between align-items-center py-2 order-bar" v-if="orderChanged">
          <span>
            <i class="bi bi-exclamation-triangle-fill"></i>
            게시순서가 변경되었습니다. <b>저장해야 메인에 반영</b>됩니다.
          </span>
          <span>
            <button type="button" class="btn btn-sm btn-outline-secondary me-2" @click="cancelOrder" :disabled="savingOrder">취소</button>
            <button type="button" class="btn btn-sm btn-success" @click="saveOrder" :disabled="savingOrder">
              <i class="bi bi-save"></i> {{ savingOrder ? '저장중...' : '순서 저장' }}
            </button>
          </span>
        </div>

        <!-- 게시리스트 -->
        <table class="tb_insta table table-bordered table-striped align-middle">
          <thead>
            <tr>
              <th style="width: 9%">게시순서</th>
              <th style="width: 19%">게시명</th>
              <th>링크정보</th>
              <th style="width: 12%">게시일</th>
              <th style="width: 9%">게시상태</th>
              <th style="width: 8%">상태변경</th>
              <th style="width: 7%">삭제</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="instaList.length === 0">
              <td colspan="7" class="text-muted py-4">등록된 인스타그램 링크가 없습니다</td>
            </tr>
            <tr v-for="(ig, i) in instaList" :key="ig.seqInsta" :class="{ 'row-moved': movedSeq === ig.seqInsta }">
              <!-- 게시순서 -->
              <td>
                <div class="order-cell">
                  <span class="order-no">{{ i + 1 }}</span>
                  <div class="btn-group-vertical btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary order-btn"
                            :disabled="i === 0 || savingOrder" @click="moveRow(i, -1)" title="위로">
                      <i class="bi bi-caret-up-fill"></i>
                    </button>
                    <button type="button" class="btn btn-outline-secondary order-btn"
                            :disabled="i === instaList.length - 1 || savingOrder" @click="moveRow(i, 1)" title="아래로">
                      <i class="bi bi-caret-down-fill"></i>
                    </button>
                  </div>
                </div>
              </td>
              <td class="text-start">{{ ig.igTitle }}</td>
              <td class="text-start">
                <span class="badge me-2" :class="typeBadge(ig.igType)">{{ typeLabel(ig.igType) }}</span>
                <a :href="ig.igUrl" target="_blank" rel="noopener">{{ ig.igUrl }}</a>
              </td>
              <td>{{ getDateFormat(ig.insDate) }}</td>
              <td>
                <span class="badge" :class="ig.useFlag === 'Y' ? 'bg-success' : 'bg-secondary'">
                  {{ ig.useFlag === 'Y' ? '게시중' : '게시중지' }}
                </span>
              </td>
              <td>
                <button type="button" class="btn btn-warning btn-sm" @click="changeStatus(ig)">변경</button>
              </td>
              <td>
                <button type="button" class="btn btn-outline-danger btn-sm" @click="deleteInsta(ig)" :disabled="savingOrder">
                  <i class="bi bi-trash3"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  data() {
    return {
      instaList: [],          // 인스타 게시리스트

      showInsertForm: false,  // 등록폼 표시여부
      newTitle: "",           // 등록: 게시명
      newUrl: "",             // 등록: 인스타 링크
      saving: false,          // 등록 저장중 (중복클릭 방지)

      orderChanged: false,    // 게시순서 변경여부 (미저장)
      savingOrder: false,     // 순서 저장중
      movedSeq: null,         // 방금 이동한 행 (하이라이트용)
      movedTimer: null,
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },

    // 메인노출 건수
    activeCount() {
      return this.instaList.filter(item => item.useFlag === "Y").length;
    },

    // 입력중인 링크 실시간 파싱
    parsedNewUrl() {
      return this.parseInstaUrl(this.newUrl);
    },
  },

  created() {
    this.user = this.$store.state.user;
    this.getInstaList();
  },

  mounted() {
    // 권한없는 로그인 차단
    if (this.user.handlerGrade >= 20 || this.user.handlerGrade == undefined) {
      var swal = require("sweetalert2");
      swal.fire({
        title: "로그인 필요",
        text: "접근권한 확인불가.",
        icon: "error",
      });
      this.$router.push({ path: "/b2bLogin" });
      return;
    }

    const adminEl = document.getElementById("admin");
    const userEl = document.getElementById("user");
    if (adminEl) adminEl.style.display = "block";
    if (userEl) userEl.style.display = "none";
  },

  // 순서 미저장 상태로 페이지 이탈 시 확인
  beforeRouteLeave(to, from, next) {
    if (this.orderChanged) {
      const leave = window.confirm("저장하지 않은 게시순서 변경이 있습니다.\n저장하지 않고 이동할까요?");
      if (!leave) return next(false);
    }
    next();
  },

  beforeUnmount() {
    clearTimeout(this.movedTimer);
  },

  methods: {
    getDateFormat(date) {
      return this.$dateFormat(date);
    },

    // 인스타 URL 파싱: 유형(p/reel/tv) + shortcode 추출, 공유파라미터 제거
    parseInstaUrl(url) {
      if (!url) return null;
      const match = url.match(/instagram\.com\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
      if (!match) return null;
      const [, type, code] = match;
      return {
        type,
        code,
        url: `https://www.instagram.com/${type}/${code}/`,
      };
    },

    typeLabel(type) {
      if (type === "reel") return "릴스";
      if (type === "tv") return "TV";
      return "게시물";
    },

    typeBadge(type) {
      if (type === "reel") return "bg-danger";
      if (type === "tv") return "bg-dark";
      return "bg-primary";
    },

    // 게시리스트 조회
    async getInstaList() {
      let instaList = await this.$api("/api/selInstaList_ma", {});
      this.instaList = Array.isArray(instaList) ? instaList : [];
      this.orderChanged = false;
    },

    // 순서 미저장 상태에서 다른 작업 차단
    blockIfOrderChanged() {
      if (this.orderChanged) {
        this.$swal.fire("", "변경된 게시순서를 먼저 <b>저장</b>하거나 <b>취소</b>해주세요", "warning");
        return true;
      }
      return false;
    },

    toggleInsertForm() {
      this.showInsertForm = !this.showInsertForm;
      if (!this.showInsertForm) {
        this.newTitle = "";
        this.newUrl = "";
      }
    },

    // 신규등록
    async insertInsta() {
      if (this.saving) return;
      if (this.blockIfOrderChanged()) return;

      if (!this.newTitle) {
        this.$swal.fire("", "게시명을 입력해주세요", "warning");
        return;
      }

      const parsed = this.parsedNewUrl;
      if (!parsed) {
        this.$swal.fire("", "올바른 인스타그램 링크가 아닙니다", "warning");
        return;
      }

      this.saving = true;
      try {
        // 중복체크
        let dup = await this.$api("/api/selInstaByCode", { param: [parsed.code] });
        if (Array.isArray(dup) && dup.length > 0) {
          const state = dup[0].useFlag === "Y" ? "게시중" : "게시중지";
          this.$swal.fire("", `이미 등록된 링크입니다<br>(${dup[0].igTitle} / ${state})`, "warning");
          return;
        }

        let res = await this.$api("/api/insInstaPost", {
          param: [this.newTitle, parsed.type, parsed.code, parsed.url, this.user.userId],
        });

        // $api는 에러를 throw하지 않으므로 반환값으로 체크
        if (!res || res.error) {
          this.$swal.fire("", "저장 중 오류가 발생했습니다", "error");
          return;
        }

        this.$swal.fire("", "등록완료!<br>맨 위(1번)에 추가되었습니다", "success");
        this.newTitle = "";
        this.newUrl = "";
        this.showInsertForm = false;
        this.getInstaList();
      } finally {
        this.saving = false;
      }
    },

    // 게시상태 변경 (Y <-> N)
    changeStatus(ig) {
      if (this.blockIfOrderChanged()) return;

      const nextFlag = ig.useFlag === "Y" ? "N" : "Y";
      const nextLabel = nextFlag === "Y" ? "게시" : "게시중지";

      this.$swal.fire({
        title: `[${ig.igTitle}]<br>${nextLabel}로 변경할까요?`,
        showCancelButton: true,
        confirmButtonText: "변경",
        cancelButtonText: "취소",
      }).then(async (result) => {
        if (result.isConfirmed) {
          let res = await this.$api("/api/changeInstaStatus_ma", {
            param: [nextFlag, this.user.userId, ig.seqInsta],
          });

          if (!res || res.error) {
            this.$swal.fire("", "변경 중 오류가 발생했습니다", "error");
            return;
          }

          this.getInstaList();
          this.$swal.fire("", "변경완료!", "success");
        }
      });
    },

    // 링크 삭제 (2026-10-30)
    deleteInsta(ig) {
      if (this.blockIfOrderChanged()) return;

      const warnActive = ig.useFlag === "Y"
        ? `<br><span style="color:#dc3545; font-weight:700;">현재 게시중인 링크로, 삭제 즉시 메인에서 사라집니다.</span>`
        : "";

      this.$swal.fire({
        title: "링크를 삭제할까요?",
        html: `<b>[${ig.igTitle}]</b><br><span style="font-size:0.85rem; color:#888;">${ig.igUrl}</span>${warnActive}<br><br>삭제한 링크는 복구할 수 없습니다.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
        confirmButtonColor: "#dc3545",
        focusCancel: true,           // 엔터로 실수 삭제 방지
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        let res = await this.$api("/api/delInstaPost_ma", { param: [ig.seqInsta] });

        // $api는 에러를 throw하지 않으므로 반환값으로 체크
        if (!res || res.error) {
          this.$swal.fire("", "삭제 중 오류가 발생했습니다", "error");
          return;
        }

        // 다른 관리자가 먼저 삭제한 경우
        if (res.affectedRows === 0) {
          this.$swal.fire("", "이미 삭제된 링크입니다", "info");
        } else {
          this.$swal.fire("", "삭제완료!", "success");
        }

        this.getInstaList();
      });
    },

    // ===== 게시순서 조정 =====
    // 행 이동 (dir: -1 위로 / 1 아래로)
    moveRow(idx, dir) {
      const target = idx + dir;
      if (target < 0 || target >= this.instaList.length) return;

      const item = this.instaList.splice(idx, 1)[0];
      this.instaList.splice(target, 0, item);
      this.orderChanged = true;

      // 이동한 행 잠깐 하이라이트
      this.movedSeq = item.seqInsta;
      clearTimeout(this.movedTimer);
      this.movedTimer = setTimeout(() => { this.movedSeq = null; }, 800);
    },

    // 순서 변경 취소 (DB 기준으로 다시 조회)
    cancelOrder() {
      this.getInstaList();
    },

    // 순서 저장: 현재 화면 순서대로 1,2,3... 부여 (변경된 행만 업데이트)
    async saveOrder() {
      if (this.savingOrder) return;

      const changedRows = this.instaList
        .map((item, i) => ({ seqInsta: item.seqInsta, newSortNo: i + 1, oldSortNo: item.sortNo }))
        .filter(row => row.newSortNo !== row.oldSortNo);

      if (changedRows.length === 0) {
        this.orderChanged = false;
        return;
      }

      this.savingOrder = true;
      let failCnt = 0;
      try {
        for (const row of changedRows) {
          let res = await this.$api("/api/updInstaSortNo_ma", {
            param: [row.newSortNo, this.user.userId, row.seqInsta],
          });
          if (!res || res.error) failCnt++;
        }
      } finally {
        this.savingOrder = false;
      }

      await this.getInstaList();

      if (failCnt > 0) {
        this.$swal.fire("", `${failCnt}건 저장에 실패했습니다.<br>현재 저장된 순서로 다시 불러왔으니 확인 후 재시도해주세요`, "error");
      } else {
        this.$swal.fire("", "게시순서 저장완료!", "success");
      }
    },
  },
};
</script>

<style scoped>
.tb_insta th {
  text-align: center;
  font-weight: bold;
  font-size: 1.05rem;
}
.tb_insta td {
  text-align: center;
  font-size: 0.95rem;
}
.tb_insta td a {
  word-break: break-all;
}
.ig-insert-card {
  border: 1px solid #f1c0c0;
  background: #fffafa;
}

/* 게시순서 */
.order-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.order-no {
  font-size: 1.1rem;
  font-weight: 700;
  min-width: 24px;
}
.order-btn {
  padding: 0 8px;
  line-height: 1.3;
}
.order-bar {
  position: sticky;
  top: 0;
  z-index: 5;
}

/* 이동한 행 하이라이트 */
.tb_insta tr.row-moved > td {
  background-color: #fff3cd !important;
  transition: background-color 0.3s ease;
}
</style>